// Layouts whose <main> scrolls instead of the window mark it with this attribute.
export const SCROLL_CONTAINER_SELECTOR = '[data-scroll-container]';

const RESTORE_TIMEOUT_MS = 1500;

type ScrollRoute = { path: string; hash: string };

export type ScrollPlan = 'keep' | 'top' | 'hash' | 'restore';

const trimSlash = (path: string) => path.replace(/\/$/, '');

// Same decision tree as Nuxt's default scrollBehavior, before route meta and timing.
export function planScroll(to: ScrollRoute, from: ScrollRoute, isHistoryNavigation: boolean): ScrollPlan {
  if (trimSlash(to.path) === trimSlash(from.path)) {
    if (to.hash) return 'hash';
    return from.hash ? 'top' : 'keep';
  }
  if (isHistoryNavigation) return 'restore';
  return to.hash ? 'hash' : 'top';
}

export function createScrollMemory() {
  const offsets = new Map<number, number[]>();
  return {
    save(key: number, tops: number[]) {
      offsets.set(key, tops);
    },
    get(key: number): number[] {
      return offsets.get(key) ?? [];
    },
  };
}

export const scrollMemory = createScrollMemory();

// vue-router stores the entry index in history.state.position.
export function historyKey(): number {
  if (typeof window === 'undefined') return 0;
  const position = (window.history.state as { position?: unknown } | null)?.position;
  return typeof position === 'number' ? position : 0;
}

export function getScrollContainers(): HTMLElement[] {
  if (typeof document === 'undefined') return [];
  return Array.from(document.querySelectorAll<HTMLElement>(SCROLL_CONTAINER_SELECTOR));
}

export function resetScrollContainers() {
  for (const el of getScrollContainers()) el.scrollTop = 0;
}

// Content often renders after the transition, so retry until the saved offset fits or the user scrolls.
export function restoreScrollContainers(tops: number[]) {
  const containers = getScrollContainers();
  const started = performance.now();
  let cancelled = false;
  const cancelEvents = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const;
  const cancel = () => {
    cancelled = true;
    cancelEvents.forEach((type) => window.removeEventListener(type, cancel));
  };
  cancelEvents.forEach((type) => window.addEventListener(type, cancel, { passive: true, once: true }));
  const apply = () => {
    if (cancelled) return;
    let pending = false;
    containers.forEach((el, i) => {
      const top = tops[i] ?? 0;
      el.scrollTop = top;
      if (Math.abs(el.scrollTop - top) > 1) pending = true;
    });
    if (pending && performance.now() - started < RESTORE_TIMEOUT_MS) requestAnimationFrame(apply);
    else cancel();
  };
  apply();
}
